// src/pages/TypingTutor.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { saveTypingLog, getTypingLogs } from '../firebase/database';
import { theme } from '../utils/theme';
import { format } from 'date-fns';
import { HINDI_LESSONS, ENGLISH_LESSONS } from '../utils/typingLessons';
import { getAllTypingLessons } from '../firebase/database';
import TypingKeyboard from '../components/TypingKeyboard';

const C = theme.colors;

// Accurate mapping for Remington GAIL
const GAIL_MAP = {
  'q': 'ु', 'w': 'ू', 'e': 'म', 'r': 'त', 't': 'ज', 'y': 'ल', 'u': 'न', 'i': 'प', 'o': 'व', 'p': 'च', '[': 'ख्', ']': 'comma',
  'a': 'ं', 's': 'ए', 'd': 'क', 'f': 'इ', 'g': 'ह', 'h': 'ी', 'j': 'र', 'k': 'ा', 'l': 'स', ';': 'य', "'": 'श',
  'z': '्र', 'x': 'ग', 'c': 'ब', 'v': 'अ', 'b': 'इ', 'n': 'ल', 'm': 'न', ',': 'ए', '.': '।', '/': 'ध',
  'Q': 'फ', 'W': 'ॅ', 'E': 'म्', 'R': 'त्', 'T': 'ज्', 'Y': 'ल्', 'U': 'न्', 'I': 'प्', 'O': 'व्', 'P': 'च्', '{': 'क्ष', '}': 'द्व',
  'A': '॑', 'S': 'ऐ', 'D': 'क्', 'F': 'थ', 'G': 'ळ', 'H': 'ी', 'J': 'श्र', 'K': 'ा', 'L': 'स्', ':': 'रू', '"': 'ष्',
  'Z': 'प्र', 'X': 'ग्', 'C': 'ब्', 'V': 'ट', 'B': 'ठ', 'N': 'छ', 'M': 'ड', '<': 'ढ', '>': 'झ', '?': 'घ'
};

export default function TypingTutor({ externalId }) {
  const { user, studentId: authStudentId } = useAuth();
  const activeId = externalId || authStudentId || user?.uid;
  const [mode, setMode] = useState('english');
  const [currentLesson, setCurrentLesson] = useState({ id: 'custom', title: 'Custom Text', content: '' });
  const [userInput, setUserInput] = useState('');
  const [customText, setCustomText] = useState('');
  const [startTime, setTime] = useState(null);
  const [stats, setStats] = useState({ wpm: 0, accuracy: 100, errors: 0, time: 0 });
  const [isFinished, setIsFinished] = useState(false);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [customLessons, setCustomLessons] = useState([]);
  const [capsLockWarning, setCapsLockWarning] = useState(false);

  const timerRef = useRef(null);
  const inputRef = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    if (activeId) loadHistory();
    loadCustomLessons();
  }, [activeId]);

  const loadCustomLessons = async () => {
    try {
      const lessons = await getAllTypingLessons();
      setCustomLessons(lessons);

      // If no lessons exist and not in custom mode, default to custom
      const all = getAllLessons(mode);
      if (all.length === 0) {
        setCurrentLesson({ id: 'custom', title: 'Custom Text', content: '' });
      } else if (currentLesson.id !== 'custom') {
        // Keep current if valid
      }
    } catch (err) {
      console.error("Failed to load custom lessons:", err);
    }
  };

  // Combine default + custom lessons
  const getAllLessons = (lessonMode) => {
    const defaults = lessonMode === 'english' ? ENGLISH_LESSONS : HINDI_LESSONS;
    const customFromDb = customLessons.filter(l => l.mode === lessonMode);
    return [...defaults, ...customFromDb];
  };

  const handleCustomTextSubmit = () => {
    if (!customText.trim()) return;
    setCurrentLesson({ id: 'custom', title: 'Custom Practice', content: customText.trim() });
    resetLesson();
  };

  // Auto-scroll logic for long lessons
  useEffect(() => {
    if (boxRef.current) {
      const activeChar = boxRef.current.querySelector('.char.current');
      if (activeChar) {
        // Keep hidden input vertically aligned but horizontally locked at 0
        if (inputRef.current) {
          inputRef.current.style.top = `${activeChar.offsetTop}px`;
          inputRef.current.style.left = '0px';
        }

        const box = boxRef.current;
        const charTop = activeChar.offsetTop;
        const boxHeight = box.clientHeight;
        const scrollTop = box.scrollTop;

        // "Page Down" behavior: When character reaches the bottom 20% of the box
        // We use behavior: 'auto' (instant) because 'smooth' can be interrupted by fast typing
        const relativePos = charTop - scrollTop;

        if (relativePos > boxHeight * 0.8) {
          // Instant jump: Move the current line to the top
          // This effectively scrolls ~6-7 lines depending on box height
          box.scrollTo({ top: charTop - 20, behavior: 'auto' });
        } 
        // If char is above the visible area (e.g. user scrolled up), bring it back instantly
        else if (relativePos < 0) {
          box.scrollTo({ top: Math.max(0, charTop - 20), behavior: 'auto' });
        }
      }
    }
  }, [userInput]);

  const loadHistory = async () => {
    if (!activeId) return;
    const logs = await getTypingLogs(activeId);
    setHistory(logs);
  };

  const startLesson = (lesson) => {
    setCurrentLesson(lesson);
    resetLesson();
  };

  const resetLesson = () => {
    setUserInput('');
    setTime(null);
    setStats({ wpm: 0, accuracy: 100, errors: 0, time: 0 });
    setIsFinished(false);
    if (timerRef.current) clearInterval(timerRef.current);
    setTimeout(() => inputRef.current?.focus(), 10);
  };

  const handleKeyDown = (e) => {
    if (isFinished || !currentLesson.content) return;

    const isCapsLockOn = e.getModifierState('CapsLock');
    if (isCapsLockOn !== capsLockWarning) {
      setCapsLockWarning(isCapsLockOn);
    }

    if (!startTime && e.key.length === 1) {
      const start = Date.now();
      setTime(start);
      timerRef.current = setInterval(() => {
        const elapsed = (Date.now() - start) / 1000;
        setStats(prev => ({ ...prev, time: Math.floor(elapsed) }));
      }, 1000);
    }

    if (mode === 'hindi' && e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
      e.preventDefault();
      const mappedChar = GAIL_MAP[e.key] || e.key;
      processChar(mappedChar);
    }
  };

  const processChar = (char) => {
    const nextInput = userInput + char;
    if (nextInput.length > currentLesson.content.length) return;
    setUserInput(nextInput);
    calculateStats(nextInput);
    if (nextInput.length === currentLesson.content.length) finishLesson(nextInput);
  };

  const handleInputChange = (e) => {
    if (mode === 'hindi' || isFinished || !currentLesson.content) return;
    const val = e.target.value;
    if (val.length > currentLesson.content.length) return;
    setUserInput(val);
    calculateStats(val);
    if (val.length === currentLesson.content.length) finishLesson(val);
  };

  const calculateStats = (input) => {
    const target = currentLesson.content;
    let errors = 0;
    for (let i = 0; i < input.length; i++) {
      if (input[i] !== target[i]) errors++;
    }
    const accuracy = input.length > 0 ? Math.max(0, Math.round(((input.length - errors) / input.length) * 100)) : 100;
    const timeInMinutes = stats.time > 0 ? stats.time / 60 : (Date.now() - startTime) / 60000;
    const wpm = timeInMinutes > 0 ? Math.round((input.length / 5) / timeInMinutes) : 0;
    setStats(prev => ({ ...prev, errors, accuracy, wpm: wpm || 0 }));
  };

  const finishLesson = async (finalInput) => {
    setIsFinished(true);
    clearInterval(timerRef.current);
    if (activeId) {
      setLoading(true);
      try {
        await saveTypingLog(activeId, {
          lessonId: currentLesson.id,
          lessonTitle: currentLesson.title,
          mode,
          speedWPM: stats.wpm,
          accuracy: stats.accuracy,
          durationSeconds: stats.time,
          date: format(new Date(), 'yyyy-MM-dd')
        });
        await loadHistory();
      } catch (err) { console.error("Error saving stats:", err); }
      finally { setLoading(false); }
    }
  };

  const targetChar = currentLesson.content[userInput.length] || '';

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', fontFamily: theme.fonts.sans, paddingBottom: 40 }}>
      <style>{`
        .typing-box { 
          font-size: 26px; 
          line-height: 1.6; 
          letter-spacing: 1px; 
          color: ${C.muted}; 
          white-space: pre-wrap; 
          word-break: break-word;
          overflow-wrap: break-word;
          position: relative; 
          padding: 24px; 
          border: 1px solid ${C.border}; 
          border-radius: 16px; 
          background: #fff; 
          box-shadow: ${theme.shadows.soft}; 
          min-height: 200px; 
          height: 320px; 
          overflow-y: auto; 
          overflow-x: hidden; 
          resize: vertical; 
          box-sizing: border-box;
        }
        .char { position: relative; font-family: 'DM Mono, monospace'; }
        .char.correct { color: ${C.text}; }
        .char.wrong { color: #ef4444; background: #fee2e2; border-radius: 2px; }
        .char.current { color: #fff; background: ${C.accent}; border-radius: 2px; position: relative; }
        .stat-card-compact { background: #fff; padding: 12px; border-radius: 10px; border: 1px solid ${C.border}; text-align: center; }
        .lesson-select { width: 100%; padding: 10px 12px; border-radius: 10px; border: 1px solid ${C.border}; background: #fff; font-size: 13px; font-weight: 500; color: ${C.text}; outline: none; cursor: pointer; transition: 0.2s; }
        .lesson-select:focus { border-color: ${C.accent}; box-shadow: 0 0 0 3px ${C.accent}22; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-thumb { background: ${C.border}; border-radius: 10px; }
      `}</style>

      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 32, alignItems: 'start' }}>
        {/* Left Side: Controls & Info */}
        <div style={{ position: 'sticky', top: 20, display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={{ padding: '0 4px' }}>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: C.text, margin: 0 }}>Typing Tutor</h1>
            <p style={{ fontSize: 13, color: C.muted, margin: '4px 0 0' }}>Master your speed & accuracy</p>
          </div>

          {/* Mode Switcher */}
          <div style={{ display: 'flex', background: C.bg, padding: 4, borderRadius: 10, border: `1px solid ${C.border}` }}>
            <button 
              onClick={() => { setMode('english'); setCurrentLesson({ id: 'custom', title: 'Custom Text', content: '' }); resetLesson(); }}
              style={{ flex: 1, padding: '8px', borderRadius: 8, border: 'none', background: mode === 'english' ? C.accent : 'none', color: mode === 'english' ? '#fff' : C.text, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}
            >English</button>
            <button 
              onClick={() => { setMode('hindi'); setCurrentLesson({ id: 'custom', title: 'Custom Text', content: '' }); resetLesson(); }}
              style={{ flex: 1, padding: '8px', borderRadius: 8, border: 'none', background: mode === 'hindi' ? C.accent : 'none', color: mode === 'hindi' ? '#fff' : C.text, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}
            >Hindi</button>
          </div>

          {/* Real-time Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="stat-card-compact">
              <div style={{ fontSize: 9, color: C.muted, fontWeight: 800, textTransform: 'uppercase' }}>WPM</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: C.accent }}>{stats.wpm}</div>
            </div>
            <div className="stat-card-compact">
              <div style={{ fontSize: 9, color: C.muted, fontWeight: 800, textTransform: 'uppercase' }}>Accuracy</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: stats.accuracy > 90 ? C.green : C.amber }}>{stats.accuracy}%</div>
            </div>
            <div className="stat-card-compact">
              <div style={{ fontSize: 9, color: C.muted, fontWeight: 800, textTransform: 'uppercase' }}>Time</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: C.text }}>{stats.time}s</div>
            </div>
            <div className="stat-card-compact">
              <div style={{ fontSize: 9, color: C.muted, fontWeight: 800, textTransform: 'uppercase' }}>Errors</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: stats.errors > 0 ? '#ef4444' : C.text }}>{stats.errors}</div>
            </div>
          </div>

          {/* Lesson Selector */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, padding: '0 4px' }}>
              <h3 style={{ fontSize: 11, color: C.muted, fontWeight: 800, textTransform: 'uppercase', margin: 0 }}>Select Lesson</h3>
              <span style={{ fontSize: 10, color: C.accent, fontWeight: 700 }}>{getAllLessons(mode).length + 1} Total</span>
            </div>
            <select 
              className="lesson-select"
              value={currentLesson.id}
              onChange={(e) => {
                if (e.target.value === 'custom') {
                  setCurrentLesson({ id: 'custom', title: 'Custom Text', content: '' });
                  resetLesson();
                  return;
                }
                const list = getAllLessons(mode);
                const selected = list.find(l => l.id === e.target.value);
                if (selected) startLesson(selected);
              }}
            >
              <option value="custom">✨ Custom / Paste Text</option>
              {getAllLessons(mode).map((lesson, idx) => (
                <option key={lesson.id} value={lesson.id}>
                  {idx + 1}. {lesson.title}
                </option>
              ))}
            </select>
          </div>

          {/* Recent History */}
          <div style={{ marginTop: 10 }}>
            <h3 style={{ fontSize: 11, color: C.muted, fontWeight: 800, textTransform: 'uppercase', marginBottom: 12, padding: '0 4px' }}>Recent Performance</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {history.slice(0, 3).map(log => (
                <div key={log.id} style={{ padding: '8px 10px', borderRadius: 8, border: `1px solid ${C.border}`, background: '#fff', fontSize: 11 }}>
                  <div style={{ fontWeight: 700, marginBottom: 2 }}>{log.lessonTitle}</div>
                  <div style={{ display: 'flex', gap: 10, color: C.muted }}>
                    <span style={{ color: C.accent, fontWeight: 700 }}>{log.speedWPM} WPM</span>
                    <span>{log.accuracy}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Side: Main Typing Zone */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {currentLesson.id === 'custom' && !currentLesson.content ? (
            <div style={{ background: '#fff', padding: 24, borderRadius: 16, border: `1px solid ${C.border}`, boxShadow: theme.shadows.soft }}>
              <h3 style={{ marginTop: 0, fontSize: 16, fontWeight: 700 }}>Paste your practice text</h3>
              <textarea 
                placeholder="Paste your text here (English or Hindi)..."
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
                rows={8}
                style={{ width: '100%', padding: 12, borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 16, outline: 'none', fontFamily: 'inherit', marginBottom: 16 }}
              />
              <button 
                onClick={handleCustomTextSubmit}
                disabled={!customText.trim()}
                style={{ padding: '12px 24px', borderRadius: 10, background: C.accent, color: '#fff', border: 'none', fontWeight: 700, cursor: 'pointer', opacity: customText.trim() ? 1 : 0.5 }}
              >Start Custom Practice</button>
            </div>
          ) : (
            <>
              <div className="typing-box" ref={boxRef} onClick={() => inputRef.current?.focus()}>
                 {capsLockWarning && (
                   <div style={{ color: '#ef4444', backgroundColor: '#fee2e2', padding: '6px', borderRadius: '8px', marginBottom: '8px', textAlign: 'center', fontWeight: 'bold', fontSize: '12px' }}>
                     Caps Lock is On! Watch out for accidental uppercase input.
                   </div>                 )}
                 {/* Hidden Input at top to prevent auto-scrolling to the end of the container */}
                 <input
                   ref={inputRef}
                   type="text"
                   value={mode === 'hindi' ? '' : userInput}
                   onChange={handleInputChange}
                   onKeyDown={handleKeyDown}
                   autoFocus
                   style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', top: 0, left: 0 }}
                 />                {currentLesson.content.split('').map((char, i) => {
                  let status = '';
                  if (i < userInput.length) status = userInput[i] === char ? 'correct' : 'wrong';
                  else if (i === userInput.length) status = 'current';
                  return <span key={i} className={`char ${status}`}>{char}</span>;
                })}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 10px' }}>
                 <button onClick={resetLesson} style={{ padding: '8px 20px', borderRadius: 10, background: '#fff', border: `1px solid ${C.border}`, color: C.text, fontWeight: 600, cursor: 'pointer', fontSize: 12 }}>Restart (Esc)</button>
                 <div style={{ fontSize: 12, color: C.muted }}>
                   Progress: <b>{userInput.length}</b> / {currentLesson.content.length} characters
                 </div>
              </div>

              {/* Visual Keyboard (Scaled Down) */}
              <TypingKeyboard targetChar={targetChar} mode={mode} />

              {mode === 'hindi' && (
                <div style={{ padding: '12px 16px', borderRadius: 10, background: C.bg, fontSize: 11, color: C.muted, border: `1px solid ${C.border}` }}>
                  <b>Hindi Tip:</b> Remington GAIL maps your physical keys: <b>D</b>=क, <b>K</b>=ा, <b>H</b>=ी. Shift for half-chars.
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {/* Result Overlay */}
      {isFinished && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(2px)' }}>
          <div className="fadeUp" style={{ background: '#fff', padding: 40, borderRadius: 24, textAlign: 'center', maxWidth: 400, width: '90%', boxShadow: '0 20px 50px rgba(0,0,0,0.3)' }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>🏆</div>
            <h2 style={{ fontSize: 24, fontWeight: 800, color: C.text, marginBottom: 8 }}>Well Done!</h2>
            <p style={{ color: C.muted, fontSize: 14, marginBottom: 24 }}>Session stats saved to your profile.</p>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 }}>
              <div style={{ padding: 16, background: C.bg, borderRadius: 12 }}>
                <div style={{ fontSize: 10, color: C.muted, fontWeight: 700 }}>SPEED</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: C.accent }}>{stats.wpm} WPM</div>
              </div>
              <div style={{ padding: 16, background: C.bg, borderRadius: 12 }}>
                <div style={{ fontSize: 10, color: C.muted, fontWeight: 700 }}>ACCURACY</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: C.green }}>{stats.accuracy}%</div>
              </div>
            </div>

            <button 
              onClick={resetLesson}
              style={{ width: '100%', padding: '14px', borderRadius: 12, background: C.accent, color: '#fff', border: 'none', fontSize: 16, fontWeight: 800, cursor: 'pointer' }}
            >Practice Next</button>
          </div>
        </div>
      )}
    </div>
  );
}
